/**
 * API key scopes, "resource:action" (ADR-7.2). One list for the settings UI, the API and the
 * docs. A scope is granted exactly, or by `*`. `partner:*` scopes only ever work on partner
 * keys, `*` included, so an org key can never act as a reseller.
 */
export const API_SCOPES = [
	{
		scope: "chat:write",
		group: "Chat",
		label: "Chat with the agent",
		hint: "Send shopper messages and voice turns, and read or change a conversation's basket.",
	},
	{
		scope: "conversations:read",
		group: "Chat",
		label: "Read conversations",
		hint: "Fetch a conversation's messages and basket.",
	},
	{
		scope: "sessions:write",
		group: "Chat",
		label: "Open shopper sessions",
		hint: "Create one-time links that open the hosted chat for a signed-in shopper.",
	},
	{
		scope: "catalog:read",
		group: "Catalog",
		label: "Read catalog runs",
		hint: "Check the status and errors of a catalog import.",
	},
	{
		scope: "catalog:write",
		group: "Catalog",
		label: "Update the catalog",
		hint: "Push products, prices and stock, or upload a product feed.",
	},
	{
		scope: "orders:read",
		group: "Orders",
		label: "Read orders",
		hint: "Look up orders and their status, including orders placed through Hephon.",
	},
	{
		scope: "orders:write",
		group: "Orders",
		label: "Report orders",
		hint: "Report placed orders and status changes, so sales are attributed to the agent.",
	},
	{
		scope: "shoppers:write",
		group: "Privacy",
		label: "Delete shopper data",
		hint: "Erase everything stored about a shopper, on their request.",
	},
	{
		scope: "usage:read",
		group: "Usage",
		label: "Read usage",
		hint: "Daily spend by kind, as JSON or CSV.",
	},
	{
		scope: "mcp:use",
		group: "Agents",
		label: "Use the MCP endpoint",
		hint: "Let an AI assistant search products, fill baskets and ask your agent through MCP. Reading orders or naming a shopper also needs Read orders.",
	},
	{
		scope: "partner:read",
		group: "Partner",
		label: "Read retailers",
		hint: "List the retailers you manage.",
		partnerOnly: true,
	},
	{
		scope: "partner:write",
		group: "Partner",
		label: "Manage retailers",
		hint: "Create retailers and their keys.",
		partnerOnly: true,
	},
	{
		scope: "*",
		group: "Full access",
		label: "Everything",
		hint: "Every scope, including ones added later. Prefer only what the integration needs.",
	},
] as const satisfies readonly {
	scope: string;
	group: string;
	label: string;
	hint: string;
	partnerOnly?: boolean;
}[];

export type ApiScope = (typeof API_SCOPES)[number]["scope"];

export const API_SCOPE_VALUES = API_SCOPES.map((s) => s.scope) as [
	ApiScope,
	...ApiScope[],
];

export interface ApiScopeOption {
	scope: ApiScope;
	group: string;
	label: string;
	hint: string;
}

/** Scopes an organization key can hold (partner scopes are for partner keys only). */
export const ORG_SCOPES: readonly ApiScopeOption[] = API_SCOPES.filter(
	(s) => !("partnerOnly" in s),
);

export const ORG_SCOPE_VALUES = ORG_SCOPES.map((s) => s.scope) as [
	ApiScope,
	...ApiScope[],
];

export interface ScopeHolder {
	kind: "org" | "partner";
	scopes: readonly string[];
}

/** Exact match or `*`. `partner:*` scopes additionally need a partner key, even with `*`. */
export function hasScope(holder: ScopeHolder, required: ApiScope): boolean {
	if (required.startsWith("partner:") && holder.kind !== "partner") {
		return false;
	}
	return holder.scopes.includes("*") || holder.scopes.includes(required);
}
